import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import { Manulife, ManulifeFr } from '@manulife/mux-logos';
import PropTypes from '../../../node_modules/prop-types/index.js';
import SkipLinks from '../SideNav/shared/SkipLinks.js';
import Tabs from '../SideNav/shared/Tabs.js';
import i18n from '../../lang/i18n/index.js';
import LANG from '../../lang/lang.js';
import { BREAKPOINTS } from '../../spacing/index.js';
import Element from '../../utils/Element.js';
import { safeRest, noop } from '../../utils/functions.js';
import { HEADER_VARIANT } from './constants.js';
import { Root, Nav, LogoComponent, HeaderButtons } from './styledComponents.js';
import useTheme from '../../hooks/useTheme.js';
import useLang from '../../hooks/useLang.js';
import useNavigation from '../../hooks/useNavigation.js';
import useMedia from '../../hooks/useMedia.js';

var _excluded = ["id", "variant", "logo", "logoProps", "useImageAsBackground", "headerTabs", "selectedTab", "onChangeHeaderTabs", "children", "mainContentRef", "mainNavRef", "subNavRef", "footerRef", "skipLinks"];

var UtilityHeader = function UtilityHeader(_ref) {
  var id = _ref.id,
      variant = _ref.variant,
      defaultLogo = _ref.logo,
      logoProps = _ref.logoProps,
      useImageAsBackground = _ref.useImageAsBackground,
      headerTabs = _ref.headerTabs,
      defaultSelectedTab = _ref.selectedTab,
      onChangeHeaderTabs = _ref.onChangeHeaderTabs,
      children = _ref.children,
      mainContentRef = _ref.mainContentRef,
      mainNavRef = _ref.mainNavRef,
      subNavRef = _ref.subNavRef,
      footerRef = _ref.footerRef,
      skipLinks = _ref.skipLinks,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();
  var lang = useLang();

  var _useNavigation = useNavigation(),
      isSignedIn = _useNavigation.isSignedIn;

  var safeProps = safeRest(rest);
  var showHamburgerMenu = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")], // media queries
  [true], // values corresponding to media query
  false // default value
  );

  var logo = function logo() {
    if (defaultLogo) {
      return defaultLogo;
    }

    var LogoEn = variant === HEADER_VARIANT.MAIN ? Manulife.WORDMARK_WHITE : Manulife.WORDMARK_BLACK;
    var LogoFr = variant === HEADER_VARIANT.MAIN ? ManulifeFr.WORDMARK_WHITE : ManulifeFr.WORDMARK_BLACK;
    var logoStyle = {
      height: '30px'
    };
    return lang === LANG.FR_CA ? /*#__PURE__*/React.createElement(LogoFr, {
      style: logoStyle,
      "data-testid": "header-logo-fr"
    }) : /*#__PURE__*/React.createElement(LogoEn, {
      style: logoStyle,
      "data-testid": "header-logo-en"
    });
  };

  return !showHamburgerMenu && /*#__PURE__*/React.createElement(Root, null, /*#__PURE__*/React.createElement(Nav, _extends({
    id: id,
    "data-testid": "header-nav",
    "aria-label": i18n[lang].utilityHeader.navAriaLabel,
    theme: theme,
    variant: variant,
    useImageAsBackground: useImageAsBackground
  }, safeProps), theme.utilityHeader.showLogo && /*#__PURE__*/React.createElement(LogoComponent, logoProps, logo()), skipLinks || /*#__PURE__*/React.createElement(SkipLinks, {
    mainContentNode: mainContentRef === null || mainContentRef === void 0 ? void 0 : mainContentRef.current,
    mainNavNode: mainNavRef === null || mainNavRef === void 0 ? void 0 : mainNavRef.current,
    subNavNode: subNavRef === null || subNavRef === void 0 ? void 0 : subNavRef.current,
    footerNode: footerRef === null || footerRef === void 0 ? void 0 : footerRef.current
  }), !isSignedIn && headerTabs.length > 0 && /*#__PURE__*/React.createElement(Tabs, {
    tabs: headerTabs,
    selectedTab: defaultSelectedTab,
    onChange: onChangeHeaderTabs,
    variant: variant
  }), children && /*#__PURE__*/React.createElement(HeaderButtons, {
    variant: variant,
    theme: theme
  }, React.Children.map(children, function (child) {
    return /*#__PURE__*/React.createElement("li", null, child);
  }))));
};

process.env.NODE_ENV !== "production" ? UtilityHeader.propTypes = {
  /** id for the nav DOM element */
  id: PropTypes.string,

  /** ONLY meant for use with Global and JH themes */
  variant: PropTypes.oneOf([HEADER_VARIANT.MAIN, HEADER_VARIANT.SUB]),

  /**
   * ONLY meant for use with Global and JH themes; wordmark logo that appears on the left side on desktops.
   * Recommended to use with `@manulife/mux-logos` library
   * */
  logo: PropTypes.node,

  /** ONLY meant for use with Global and JH themes; props to set a11y and control interaction with the wordmark logo */
  logoProps: PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func
  }),

  /** ONLY meant for use with Global and JH themes; flag to set background to transparent for MAIN variant */
  useImageAsBackground: PropTypes.bool,

  /** Tabs to be displayed in the header on desktops */
  headerTabs: PropTypes.arrayOf(PropTypes.shape({
    /** component to use as tab ('a', 'button', or Link from react-router-dom) */
    as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

    /** id for the DOM element of the tab */
    id: PropTypes.string.isRequired,

    /** label to be displayed on the tab */
    label: PropTypes.node.isRequired,

    /** value used to determine selectedTab */
    value: PropTypes.string.isRequired,

    /** url (by default maps to href) or url/object/function (when using Link from react-router-dom) */
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),

    /** flag to indicate an external link that will open in a new tab */
    isExternal: PropTypes.bool
  })),

  /** value of selected header tab */
  selectedTab: PropTypes.string,

  /** onChange handler for header tabs */
  onChangeHeaderTabs: PropTypes.func,

  /** must be one or more of ContactUs, Lang, Search, SignIn, or HeaderButton component */
  children: PropTypes.node,

  /** Ref of the main content area **(to be deprecated; use skipLinks prop instead)** */
  mainContentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),

  /** Ref of the first main nav item **(to be deprecated; use skipLinks prop instead)** */
  mainNavRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),

  /** Ref of the first sub nav item **(to be deprecated; use skipLinks prop instead)** */
  subNavRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),

  /** Ref of the first footer item **(to be deprecated; use skipLinks prop instead)** */
  footerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),

  /** SkipLinks component (rendered in desktop view only, use SideNav for mobile view) */
  skipLinks: PropTypes.node
} : void 0;
UtilityHeader.defaultProps = {
  id: undefined,
  variant: HEADER_VARIANT.MAIN,
  logo: undefined,
  logoProps: {
    href: undefined,
    onClick: noop
  },
  useImageAsBackground: false,
  headerTabs: [],
  selectedTab: undefined,
  onChangeHeaderTabs: noop,
  children: undefined,
  mainContentRef: undefined,
  mainNavRef: undefined,
  subNavRef: undefined,
  footerRef: undefined,
  skipLinks: undefined
};
process.env.NODE_ENV !== "production" ? UtilityHeader.displayName = 'UtilityHeader' : void 0;
var UtilityHeader$1 = UtilityHeader;

export { UtilityHeader$1 as default };
